import { useEffect, useRef, useState, DependencyList } from "react";
import { fetchKmsData } from "@kaltura/mediaspace-shared-utils";

/**
 * Custom hook for fetching data from KMS endpoints with memoization NOT using
 * useMemo, because it has a problem in combination with lazy loading and suspense.
 * see https://github.com/facebook/react/issues/24935.
 *
 * @param options {url The URL to fetch data from, data optional data to send as the request body, optional boolean making the request abortable}
 * @param dependencies Array of dependencies that should trigger a refetch
 * @returns The fetched data
 */
export function useFetchKmsData<T>(
    options: { url: string; data?: unknown; abortable?: boolean },
    dependencies: DependencyList
) {
    // The initial return value is a function that throws a pending promise,
    // matching the Suspense pattern in fetchData.
    const fetchDataRef = useRef<() => T>(() => {
        throw new Promise(() => {});
    });

    const [, triggerRefresh] = useState<number>(0);

    useEffect(
        () => {
            // update the ref with the real fetch promise
            fetchDataRef.current = fetchKmsData<T>(options.url, options.data, options.abortable);
            // increment to force re-render
            triggerRefresh((prev) => prev + 1);
        },
        dependencies
    );

    return fetchDataRef.current;
}
